import Avatar from '@mui/material/Avatar'
import Grid from '@mui/material/Grid'
import _ from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Placeholder from '../../../../../images/placeholder120x120.png'
import { getProductUrl } from '../../../urls'
import QuantityInput from '../QuantityInput'
import VariantPrice from './VariantPrice'
import VariantsSelect from './VariantsSelect'
import {
  ISupplyDataProduct,
  ISupplyDataVariant,
  ISupplyFormData,
} from '@components/Product/VariantKitPanel/VariantKitPanel'
import { useTranslation } from 'react-i18next'
import { MAX_PRODUCT_QTY, MAX_WHOLESALE_PRODUCT_QTY } from '../../../../constants'

interface VariantPayload {
  supplyProductPk: number
  pk: number
  quantity: number
}

interface Props {
  supplyProduct: ISupplyDataProduct
  variants: ISupplyDataVariant[]
  moveTop(supplyProductPk: number): void
  moveToBottom(supplyProductPk: number): void
  removeVariant(supplyVariantPk: number): void
  addVariant(supplyVariantPk: number, data: VariantPayload): void
  onClosePanel(): void
  isWholesale: boolean
}

const VariantDetails: React.FC<Props> = ({
  supplyProduct,
  variants,
  moveTop,
  moveToBottom,
  removeVariant,
  addVariant,
  onClosePanel,
  isWholesale,
}) => {
  const { t } = useTranslation()
  const initialVariant = variants.find(el => el.isInCart) || variants[0]
  const [selected, setSelected] = useState<ISupplyDataVariant>(initialVariant)
  const [quantity, setQuantity] = useState(
    initialVariant.isInCart
      ? initialVariant.cartQuantity
      : !initialVariant.cartLine &&
        (initialVariant.stockQty || ['in', 'backorder'].includes(initialVariant.outOfStockStatus))
      ? 1
      : 0
  )

  const createDataSelected = (variant: ISupplyDataVariant, quantity: number): VariantPayload => {
    return {
      supplyProductPk: variant.supplyProductPk,
      pk: variant.supplyVariantPk,
      quantity: quantity,
    }
  }

  const changeQuantity = (newQty: number) => {
    if (quantity > 0 && newQty <= 0) {
      moveToBottom(selected.supplyProductPk)
    } else if (quantity === 0 && newQty > 0) {
      moveTop(selected.supplyProductPk)
    }
    if (newQty <= 0) {
      removeVariant(selected.supplyVariantPk)
    } else {
      const data = createDataSelected(selected, newQty)
      addVariant(selected.supplyVariantPk, data)
    }
    setQuantity(newQty)
  }

  const onChange = e => {
    const prev = selected
    const val = e.target.value
    removeVariant(prev.supplyVariantPk)
    const variant = _.find(variants, o => String(o.supplyVariantPk) === val)
    if (variant) {
      setSelected(variant)
      if (variant.stockQty < quantity) {
        changeQuantity(variant.stockQty)
      }
      const newQty = variant.stockQty < quantity ? variant.stockQty : quantity
      if (newQty > 0) {
        const data = createDataSelected(variant, newQty)
        addVariant(selected.supplyVariantPk, data)
      }
    }
  }

  const toggleDigital = () => {
    if (quantity === 0) {
      changeQuantity(1)
    } else {
      changeQuantity(0)
    }
  }

  return (
    <>
      <Grid item xs={3} className={`${quantity === 0 ? 'variant__not-added' : ''}`}>
        <Avatar
          alt={supplyProduct.specificsName}
          src={supplyProduct.imageURL || Placeholder}
          style={{ width: 'auto', height: 'auto' }}
          variant='square'
        />
      </Grid>
      <Grid item xs={9}>
        <Grid container spacing={1} className={'full-height'}>
          <Grid item xs={10} className={`${quantity === 0 ? 'variant__not-added' : ''}`}>
            <div>
              <Link to={getProductUrl(supplyProduct.specificsSlug, supplyProduct.specificsPk)} onClick={onClosePanel}>
                <span className='title'>{supplyProduct.specificsName}</span>
              </Link>
            </div>
            {(selected.stockQty > 0 || selected.outOfStockStatus === 'in') && (
              <div>
                <span className='stock-status dynamic green text-xs'>{t('productInStock', 'In Stock')}</span>
              </div>
            )}
            {selected.stockQty === 0 && selected.outOfStockStatus === 'out' && (
              <div>
                <span className='stock-status dynamic red text-xs'>{t('productOutOfStock', 'Out of Stock')}</span>
              </div>
            )}
            {selected.stockQty === 0 && selected.outOfStockStatus === 'backorder' && (
              <div>
                <span className='stock-status dynamic yellow text-xs'>{t('productBackorder', 'Backorder')}</span>
              </div>
            )}
          </Grid>
          <Grid item xs={2} className={`${quantity === 0 ? 'variant__not-added' : ''}`}>
            <VariantPrice
              price={Number(isWholesale ? selected.wholesalePrice.gross : selected.price.gross)}
              priceUndiscounted={Number(isWholesale ? selected.wholesalePrice.gross : selected.priceUndiscounted.gross)}
              currency={selected.priceUndiscounted.currency}
              quantity={quantity}
            />
          </Grid>
          <Grid item xs={12}>
            <div className='kit-form__widgets full-height'>
              <VariantsSelect variants={variants} onChange={onChange} selected={selected.supplyVariantPk} />
              {selected.isShippingRequired && (
                <QuantityInput
                  quantity={quantity}
                  availableQuantity={
                    ['backorder', 'in'].includes(selected.outOfStockStatus) ? MAX_PRODUCT_QTY : selected.stockQty
                  }
                  cartQuantity={selected.cartQuantity}
                  onChangeQuantity={changeQuantity}
                  canZero={true}
                />
              )}
              {!selected.isShippingRequired && (
                <button
                  className={`btn btn-xs ${quantity > 0 ? 'btn-secondary' : 'btn-primary'}`}
                  onClick={toggleDigital}
                >
                  {quantity > 0 ? 'Exclude' : 'Add'}
                </button>
              )}
            </div>
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}

export default VariantDetails
